import { useState, useRef } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Loader2, Upload } from "lucide-react";

export const DOC_TYPES = {
  rrhh: [["contrato", "Contrato"], ["finiquito", "Finiquito"], ["nomina", "Nómina"], ["irpf", "IRPF / Modelo 145"], ["alta_ss", "Alta Seg. Social"], ["otros", "Otros"]],
  formacion: [["curso", "Curso / Certificado"], ["titulo", "Título"], ["otros", "Otros"]],
  prl_epis: [["epi", "Entrega de EPI"], ["evaluacion", "Evaluación de riesgos"], ["formacion_prl", "Formación PRL"], ["otros", "Otros"]],
  salud: [["reconocimiento", "Reconocimiento médico"], ["aptitud", "Certificado de aptitud"], ["otros", "Otros"]],
};

const blank = (section) => ({
  user_id: "", title: "", doc_type: DOC_TYPES[section]?.[0]?.[0] || "otros",
  issue_date: "", expiry_date: "", reminder_days: 30, requires_ack: false, item: "", item_size: "", file: null,
});

export default function DocumentUploadForm({ section, employees = [], presetEmployee = "", onSaved }) {
  const [form, setForm] = useState({ ...blank(section), user_id: presetEmployee });
  const [saving, setSaving] = useState(false);
  const fileRef = useRef();
  const isEpi = section === "prl_epis" && form.doc_type === "epi";

  const submit = async () => {
    if (!form.user_id) return toast.error("Selecciona el empleado");
    if (!form.title.trim()) return toast.error("Indica un título");
    setSaving(true);
    const fd = new FormData();
    fd.append("section", section);
    fd.append("user_id", form.user_id);
    fd.append("title", form.title);
    fd.append("doc_type", form.doc_type);
    fd.append("issue_date", form.issue_date || "");
    fd.append("expiry_date", form.expiry_date || "");
    fd.append("reminder_days", form.expiry_date ? String(form.reminder_days || 30) : "30");
    fd.append("requires_ack", isEpi ? form.requires_ack : false);
    fd.append("item", form.item || "");
    fd.append("item_size", form.item_size || "");
    if (form.file) fd.append("file", form.file);
    try {
      await api.post("/documents", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Documento guardado");
      setForm({ ...blank(section), user_id: presetEmployee });
      onSaved?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-4">
      <div>
        <Label>Empleado *</Label>
        <Select value={form.user_id} onValueChange={(v) => setForm({ ...form, user_id: v })}>
          <SelectTrigger className="mt-1" data-testid="doc-employee"><SelectValue placeholder="Selecciona empleado" /></SelectTrigger>
          <SelectContent>{employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label>Tipo</Label>
          <Select value={form.doc_type} onValueChange={(v) => setForm({ ...form, doc_type: v })}>
            <SelectTrigger className="mt-1" data-testid="doc-type"><SelectValue /></SelectTrigger>
            <SelectContent>{(DOC_TYPES[section] || []).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div><Label>Título *</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1" data-testid="doc-title" placeholder="Ej. Nómina junio 2026" /></div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div><Label>Fecha emisión</Label><Input type="date" value={form.issue_date} onChange={(e) => setForm({ ...form, issue_date: e.target.value })} className="mt-1" data-testid="doc-issue" /></div>
        <div><Label>Caducidad (opcional)</Label><Input type="date" value={form.expiry_date} onChange={(e) => setForm({ ...form, expiry_date: e.target.value })} className="mt-1" data-testid="doc-expiry" /></div>
      </div>
      {form.expiry_date && (
        <div className="rounded-lg border border-amber-200 bg-amber-50/60 p-3">
          <Label className="text-amber-900">Avisar por email antes de caducar</Label>
          <div className="flex items-center gap-2 mt-1.5">
            <Input type="number" min={1} max={365} value={form.reminder_days} onChange={(e) => setForm({ ...form, reminder_days: e.target.value })} className="h-10 w-24" data-testid="doc-reminder-days" />
            <span className="text-sm text-slate-600">días antes</span>
          </div>
          <p className="text-xs text-slate-500 mt-1">Se enviará un recordatorio a la persona responsable de la empresa.</p>
        </div>
      )}
      {isEpi && (
        <div className="grid grid-cols-2 gap-3 rounded-lg border border-slate-200 p-3">
          <div><Label>EPI entregado</Label><Input value={form.item} onChange={(e) => setForm({ ...form, item: e.target.value })} className="mt-1" placeholder="Casco, botas..." data-testid="doc-item" /></div>
          <div><Label>Talla</Label><Input value={form.item_size} onChange={(e) => setForm({ ...form, item_size: e.target.value })} className="mt-1" placeholder="L / 43" data-testid="doc-item-size" /></div>
          <label className="col-span-2 flex items-center justify-between text-sm text-slate-700">
            Requiere acuse de recibo del empleado
            <Switch checked={form.requires_ack} onCheckedChange={(v) => setForm({ ...form, requires_ack: v })} data-testid="doc-ack" />
          </label>
        </div>
      )}
      <div>
        <Label>Archivo {section === "prl_epis" ? "(opcional)" : ""}</Label>
        <input type="file" hidden ref={fileRef} accept=".pdf,.jpg,.jpeg,.png,.webp,.doc,.docx,.xls,.xlsx" onChange={(e) => setForm({ ...form, file: e.target.files[0] })} data-testid="doc-file-input" />
        <div className="flex items-center gap-2 mt-1">
          <Button type="button" variant="outline" size="sm" onClick={() => fileRef.current?.click()} data-testid="doc-file-btn"><Upload className="h-4 w-4 mr-1" /> {form.file ? "Cambiar" : "Subir archivo"}</Button>
          <span className="text-xs text-slate-500 truncate">{form.file?.name || "PDF, imagen, Word o Excel (máx 10MB)"}</span>
        </div>
      </div>
      <Button onClick={submit} disabled={saving} className="brand-btn w-full" data-testid="doc-submit-btn">
        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Guardar documento"}
      </Button>
    </div>
  );
}
